import { ApolloClient, InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';
import { relayStylePagination } from '@apollo/client/utilities';

import { accessToken, clear as clearSession, currentIdentity } from '@/auth/session';
import { arcadeGraphqlEndpoint } from '@/config/runtime-config';

/**
 * The Arcade GraphQL client.
 *
 * Arcade accepts Turnstile's identity token directly, so the portal talks to
 * it with the same token it logged in with. A header the caller set on the
 * operation wins, which is how account creation sends a token that has not
 * been adopted into the session yet.
 */
export function createApolloClient(): ApolloClient {
  const auth = new SetContextLink(async (prevContext) => {
    const token = currentIdentity() ? await accessToken() : null;
    return {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...prevContext.headers,
      },
    };
  });

  // A 401 here means the session is unrecoverable — the token was refreshed
  // moments ago in the auth link above, so retrying would only spend another
  // round trip on the same answer. Dropping the identity re-renders the app at
  // the sign-in screen.
  const onUnauthenticated = new ErrorLink(({ error }) => {
    if (error && 'statusCode' in error && error.statusCode === 401) {
      clearSession();
    }
  });

  return new ApolloClient({
    link: auth.concat(onUnauthenticated).concat(new HttpLink({ uri: arcadeGraphqlEndpoint() })),
    cache: new InMemoryCache({
      typePolicies: { ApiKey: { fields: { requests: relayStylePagination(['filter']) } } },
    }),
    devtools: { enabled: true },
  });
}
